/**
 * i18n Context - Language state management
 *
 * @see docs/backlog.md Request 5
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { Language, TranslationKeys } from './types';
import { en } from './locales/en';
import { ja } from './locales/ja';

/**
 * Available translations
 */
const translations: Record<Language, TranslationKeys> = {
  en,
  ja,
};

/**
 * Get browser language preference
 */
function getBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'en';
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith('ja')) return 'ja';
  return 'en';
}

/**
 * Get stored language preference
 */
function getStoredLanguage(): Language | null {
  if (typeof localStorage === 'undefined') return null;
  const stored = localStorage.getItem('aos-language');
  if (stored === 'en' || stored === 'ja') return stored;
  return null;
}

/**
 * Store language preference
 */
function storeLanguage(lang: Language): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem('aos-language', lang);
}

/**
 * Context value type
 */
interface I18nContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: TranslationKeys;
}

/**
 * I18n Context
 */
const I18nContext = createContext<I18nContextValue | null>(null);

/**
 * I18n Provider component
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  // Initialize with stored preference, browser language, or default to English
  const [language, setLanguageState] = useState<Language>(() => {
    return getStoredLanguage() ?? getBrowserLanguage();
  });

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    storeLanguage(lang);
  }, []);

  const value: I18nContextValue = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * Hook to access i18n context
 */
export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}

/**
 * Hook to access translations only (for components that don't need to change language)
 */
export function useTranslations(): TranslationKeys {
  return useI18n().t;
}

/**
 * Utility function to interpolate placeholders in translation strings
 * e.g., interpolate("Hello {name}", { name: "World" }) => "Hello World"
 */
export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return String(values[key] ?? match);
  });
}

/**
 * Validation error code to translation key mapping
 * Note: Keys must match error codes returned from buildTrackValidator.ts
 */
const buildTrackErrorMapping: Record<string, keyof TranslationKeys['buildTrack']['validation']> = {
  'PLAYER_BANKRUPT': 'playerBankrupt',
  'INVALID_TILE_TYPE': 'invalidTileType',
  'INVALID_PLACEMENT_COUNT': 'maxTilesReached',
  'NEW_CITY_LIMIT_REACHED': 'newCityLimitReached',
  'NEW_CITY_NOT_AVAILABLE': 'newCityNotAvailable',
  'OUT_OF_BOUNDS': 'hexNotFound',
  'CANNOT_BUILD_ON_CITY': 'cannotBuildOnCity',
  'CANNOT_BUILD_ON_MOUNTAINS': 'cannotBuildOnMountains',
  'CANNOT_BUILD_ON_URBANIZED_TOWN': 'cannotBuildOnUrbanizedTown',
  'TOWN_TILE_REQUIRES_TOWN_HEX': 'townTileRequiresTownHex',
  'NEW_CITY_REQUIRES_TOWN_HEX': 'newCityRequiresTownHex',
  'URBANIZATION_REQUIRED': 'urbanizationRequired',
  'URBANIZATION_ALREADY_USED': 'urbanizationAlreadyUsed',
  'TOWN_DISK_ALREADY_PLACED': 'townDiskAlreadyPlaced',
  'NO_TOWN_DISKS_REMAINING': 'noTownDisksRemaining',
  'HEX_OCCUPIED': 'hexOccupied',
  'INVALID_CONNECTION': 'mustConnectToExisting',
  'FLOATING_TRACK_PATH': 'floatingTrackPath',
  'WALL_BLOCKS_CONNECTION': 'wallBlocksConnection',
  'CANNOT_CONNECT_TO_MAP_EDGE': 'cannotConnectMapEdge',
  'CANNOT_CONNECT_TO_SEA': 'cannotConnectSea',
  'INSUFFICIENT_FUNDS': 'insufficientFunds',
  'WOULD_FORM_SELF_REFERENCING_LINK': 'selfReferencingLink',
  'INVALID_REPLACEMENT_PATTERN': 'invalidReplacementPattern',
  'INVALID_REPLACEMENT_CONNECTION': 'invalidReplacementConnection',
  'NEW_PATH_NOT_CONNECTED': 'newPathNotConnected',
  'NOT_AT_UNFINISHED_SECTION_END': 'notAtUnfinishedSectionEnd',
  'CANNOT_REPLACE_OTHER_PLAYER_TRACK': 'cannotReplaceOtherPlayerTrack',
  'REPLACEMENT_BREAKS_CONNECTION': 'replacementBreaksConnection',
  'CANNOT_REPLACE_COMPLETED_LINK': 'cannotReplaceCompletedLink',
  'CANNOT_MODIFY_OTHER_PLAYER_PATH': 'cannotModifyOtherPlayerPath',
  'PATH_NOT_AT_UNFINISHED_SECTION_END': 'pathNotAtUnfinishedSectionEnd',
  'TOWN_TILE_LIMIT_REACHED': 'townTileLimitReached',
  'NO_TRACK_TO_REPLACE': 'noTrackToReplace',
  'TOWN_EDGE_NOT_PRESERVED': 'townEdgeNotPreserved',
  'EXISTING_EDGE_NOT_IN_TOWN': 'existingEdgeNotInTown',
  'EXISTING_EDGE_NOT_PRESERVED': 'existingEdgeNotPreserved',
};

/**
 * Hook to translate validation error codes
 * Returns a function that translates error codes to localized messages
 */
export function useValidationTranslator() {
  const { t } = useI18n();

  return {
    /**
     * Translate a build track validation error
     * @param errorCode - Error code from validator
     * @param errorMessage - Original English message (fallback)
     * @param params - Optional parameters for interpolation
     */
    translateBuildTrackError: (
      errorCode: string | undefined,
      errorMessage: string | null,
      params?: Record<string, string | number>
    ): string | null => {
      if (!errorMessage && !errorCode) return null;

      if (errorCode && buildTrackErrorMapping[errorCode]) {
        const key = buildTrackErrorMapping[errorCode];
        const template = t.buildTrack.validation[key];
        return params ? interpolate(template, params) : template;
      }

      // Fallback to original message
      return errorMessage;
    },
  };
}
